import React, { useState } from "react";
import CustomSelect from "../../globalcontrols/customselect/customselect";
import "./css/monthlySalesVariability.css";

const money = (value) => new Intl.NumberFormat("es-CL", {
  style: "currency",
  currency: "CLP",
  maximumFractionDigits: 0,
}).format(Number(value || 0));

const compactMoney = (value) => `$${new Intl.NumberFormat("es-CL", {
  notation: "compact",
  maximumFractionDigits: 1,
}).format(Number(value || 0))}`;

function monthLabel(value) {
  return new Intl.DateTimeFormat("es-CL", { month: "short", year: "2-digit", timeZone: "UTC" })
    .format(new Date(value));
}

function MonthlySalesVariability({
  data,
  error,
  loading,
  locations,
  selectedLocation,
  setSelectedLocation,
  embedded = false,
}) {
  const [activePoint, setActivePoint] = useState(null);
  const series = data?.series || [];
  const max = Number(data?.max_upper_bound || 0) || 1;
  const chartHeight = 240;
  const top = 14;
  const bottom = 38;
  const plotHeight = chartHeight - top - bottom;
  const width = Math.max(780, series.length * 68 + 76);
  const left = 62;
  const plotWidth = width - left - 18;
  const y = (value) => top + plotHeight - (Number(value || 0) / max) * plotHeight;

  return (
    <section
      className={`${embedded ? "embedded-dashboard-chart" : "dashboard-card"} monthly-variability-card`}
      aria-labelledby="monthly-variability-title"
    >
      <div className="card-heading monthly-variability-heading">
        <div>
          <p className="card-kicker">Variabilidad mensual</p>
          <h2 id="monthly-variability-title">Promedio diario por mes</h2>
          <p>El punto muestra el promedio; la línea vertical representa ±1 desviación estándar.</p>
        </div>
        <div className="monthly-variability-location">
          <CustomSelect
            label="Ubicación"
            options={locations}
            selectedValue={selectedLocation}
            onChange={setSelectedLocation}
            labelExtractor={(item) => item.name}
            valueExtractor={(item) => item.id.toString()}
          />
        </div>
      </div>

      {error ? <div className="dashboard-alert" role="alert">{error}</div> : null}
      {loading ? <div className="dashboard-loading" role="status">Calculando variabilidad…</div> : (
        <div className="monthly-variability-scroll">
          <div
            className="monthly-variability-stage"
            style={{ width }}
            onMouseLeave={() => setActivePoint(null)}
          >
            <svg
              className="monthly-variability-chart"
              viewBox={`0 0 ${width} ${chartHeight}`}
              role="img"
              aria-label="Promedio diario de ventas por mes con una desviación estándar hacia arriba y abajo"
            >
            {[0, 0.5, 1].map((ratio) => {
              const gridY = y(max * ratio);
              return (
                <g key={ratio}>
                  <line x1={left} x2={width - 18} y1={gridY} y2={gridY} className="monthly-grid-line" />
                  <text x={left - 8} y={gridY + 4} textAnchor="end" className="monthly-axis-label">
                    {compactMoney(max * ratio)}
                  </text>
                </g>
              );
            })}
            {series.map((point, index) => {
              const x = left + ((index + 0.5) / series.length) * plotWidth;
              const averageY = y(point.average_daily_revenue);
              const upperY = y(point.upper_bound);
              const lowerY = y(point.lower_bound);
              const title = `${monthLabel(point.month)} · promedio ${money(point.average_daily_revenue)} · desviación ${money(point.revenue_stddev)} · ${point.sampled_days} días`;
              return (
                <g
                  key={point.month}
                  className="monthly-point"
                  tabIndex="0"
                  onMouseEnter={() => setActivePoint({ ...point, x })}
                  onFocus={() => setActivePoint({ ...point, x })}
                >
                  <title>{title}</title>
                  <line x1={x} x2={x} y1={upperY} y2={lowerY} className="monthly-whisker" />
                  <line x1={x - 7} x2={x + 7} y1={upperY} y2={upperY} className="monthly-whisker-cap" />
                  <line x1={x - 7} x2={x + 7} y1={lowerY} y2={lowerY} className="monthly-whisker-cap" />
                  <circle cx={x} cy={averageY} r="5" className="monthly-average-dot" />
                  <text x={x} y={chartHeight - 13} textAnchor="middle" className="monthly-month-label">
                    {monthLabel(point.month)}
                  </text>
                </g>
              );
            })}
            </svg>
            {activePoint ? (
              <aside
                className="chart-product-popover monthly-product-popover"
                style={{ left: Math.min(width - 270, Math.max(8, activePoint.x - 125)) }}
              >
                <header><strong>{monthLabel(activePoint.month)}</strong><span>Productos más vendidos</span></header>
                <div className="monthly-popover-summary">
                  <span>Promedio <strong>{money(activePoint.average_daily_revenue)}</strong></span>
                  <span>Intervalo ±1σ <strong>{money(activePoint.lower_bound)} – {money(activePoint.upper_bound)}</strong></span>
                </div>
                <div>
                  {(activePoint.products || []).length ? activePoint.products.map((product) => (
                    <article key={product.code}>
                      <span><strong>{product.product}</strong><small>{product.units.toLocaleString("es-CL")} unidades</small></span>
                      <strong>{money(product.revenue)}</strong>
                    </article>
                  )) : <p>Sin productos vendidos.</p>}
                </div>
              </aside>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}

export default MonthlySalesVariability;
